import { useState, useEffect } from 'react';
import { Layout } from '../../components/layout/Layout';
import api from '../../api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent } from '../../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { 
  Plus, Eye, Edit, Copy, Trash2, ChevronLeft, ChevronRight,
  ChevronUp, ChevronDown, ChevronsUpDown,
  BatteryCharging, Zap, Building2
} from 'lucide-react';
import { usePermissions } from '../../hooks/usePermissions';
import { useBulkSelection } from '../../hooks/useBulkSelection';
import BulkEditBar from '../../components/BulkEditBar';
import ExportButton from '../../components/ExportButton';
import { Checkbox } from '../../components/ui/checkbox';
import { toast } from 'sonner';
import SelectWithCreate from '../../components/ui/select-with-create';

const Ups = () => {
  const { canRead, canCreate, canUpdate, canDelete } = usePermissions('assets', 'ups');
  const [upsList, setUpsList] = useState([]);
  const [stats, setStats] = useState({ total: 0, apc: 0, eaton: 0, otros: 0, potencia_total_kva: 0 });
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [filters, setFilters] = useState({ search: '', site: '', fabricante: '', status_id: '' });
  const [searchInput, setSearchInput] = useState(''); // Input inmediato para debounce
  const [sortConfig, setSortConfig] = useState({ field: 'name', dir: 'asc' });
  const [activeStatFilter, setActiveStatFilter] = useState('all');
  const [pageSize, setPageSize] = useState(25);

  // Form options
  const [options, setOptions] = useState({ modelos: [], sites: [], ubicaciones: [], proveedores: [], estados: [] });
  
  // Filter options (only values that exist in ups table)
  const [filterOptions, setFilterOptions] = useState({ fabricantes: [], sites: [], estados: [] });

  // Bulk selection
  const { 
    selectedIds, selectedCount, isAllSelected, isSomeSelected, 
    isSelected, toggleRow, toggleAll, clearSelection 
  } = useBulkSelection(upsList);

  // Modals
  const [viewModal, setViewModal] = useState({ open: false, ups: null });
  const [editModal, setEditModal] = useState({ open: false, ups: null, isCopy: false });
  const [deleteModal, setDeleteModal] = useState({ open: false, ups: null });
  const [formData, setFormData] = useState({
    name: '', modelo_id: '', nro_serie: '', ip_mgmt: '', site: '',
    ubicacion: '', id_proveedor: '', costo: '', ultimo_service: '', observations: '', status_id: ''
  });
  const [saving, setSaving] = useState(false);

  const fetchStats = async () => {
    try {
      const response = await api.get('/assets/ups/stats');
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const fetchOptions = async () => {
    try {
      const response = await api.get('/assets/ups/form/options');
      setOptions(response.data);
    } catch (error) {
      console.error('Error fetching options:', error);
    }
  };

  const fetchFilterOptions = async () => {
    try {
      const response = await api.get('/assets/ups/filter/options');
      setFilterOptions(response.data);
    } catch (error) {
      console.error('Error fetching filter options:', error);
    }
  };

  const fetchUpsList = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        page_size: pageSize.toString(),
        sort_by: sortConfig.field,
        sort_dir: sortConfig.dir
      });
      if (filters.search) params.append('search', filters.search);
      if (filters.site) params.append('site', filters.site);
      if (filters.fabricante) params.append('fabricante', filters.fabricante);

      const response = await api.get(`/assets/ups?${params}`);
      setUpsList(response.data.items);
      setTotalPages(response.data.pages);
      setTotal(response.data.total);
    } catch (error) {
      toast.error('Error al cargar UPS');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
    fetchOptions();
    fetchFilterOptions();
  }, []);

  useEffect(() => {
    fetchUpsList();
  }, [page, pageSize, filters.search, filters.site, filters.fabricante, filters.status_id, sortConfig]);

  // Debounce para filtro de búsqueda (800ms)
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchInput !== filters.search) {
        setFilters(prev => ({ ...prev, search: searchInput }));
        setPage(1);
      }
    }, 800);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
    setPage(1);
  };

  // Stat card click handler - filters by fabricante
  const handleStatFilter = (filterType) => {
    setActiveStatFilter(filterType);
    setPage(1);
    setSearchInput('');
    
    // Reset filters and apply the specific one
    let newFilters = { search: '', site: '', fabricante: '', status_id: '' };
    
    switch (filterType) {
      case 'apc':
        newFilters.fabricante = 'apc';
        break;
      case 'eaton':
        newFilters.fabricante = 'eaton';
        break;
      case 'otros':
        newFilters.fabricante = 'otros';
        break;
      default:
        // 'all' - keep filters empty
        break;
    }
    
    setFilters(newFilters);
  };

  // Sort handler
  const handleSort = (field) => {
    setSortConfig(prev => ({
      field,
      dir: prev.field === field && prev.dir === 'asc' ? 'desc' : 'asc'
    }));
  };

  // Sort icon component
  const SortIcon = ({ field }) => {
    if (sortConfig.field !== field) {
      return <ChevronsUpDown className="h-3 w-3 text-muted-foreground/50" />;
    }
    return sortConfig.dir === 'asc' 
      ? <ChevronUp className="h-3 w-3 text-primary" />
      : <ChevronDown className="h-3 w-3 text-primary" />;
  };

  // Fabricante badge
  const getFabricanteBadge = (fabricante) => {
    if (!fabricante) return <span className="badge badge-outline">Sin modelo</span>;
    const f = fabricante.toLowerCase();
    if (f.includes('apc')) return <span className="badge badge-success">APC</span>;
    if (f.includes('eaton')) return <span className="badge badge-info">Eaton</span>;
    if (f.includes('vertiv')) return <span className="badge badge-warning">Vertiv</span>;
    if (f.includes('schneider')) return <span className="badge badge-primary">Schneider</span>;
    return <span className="badge badge-outline">{fabricante}</span>;
  };

  // View modal
  const handleView = (ups) => {
    setViewModal({ open: true, ups });
  };

  // Edit modal
  const handleEdit = (ups) => {
    setFormData({
      name: ups.name || '',
      modelo_id: ups.modelo_id?.toString() || '',
      nro_serie: ups.nro_serie || '',
      ip_mgmt: ups.ip_mgmt || '',
      site: ups.site?.toString() || '',
      ubicacion: ups.ubicacion?.toString() || '',
      id_proveedor: ups.id_proveedor?.toString() || '',
      costo: ups.costo?.toString() || '',
      ultimo_service: ups.ultimo_service || '',
      observations: ups.observations || '',
      status_id: ups.status_id?.toString() || ''
    });
    setEditModal({ open: true, ups, isCopy: false });
  };

  // Copy modal
  const handleCopy = (ups) => {
    setFormData({
      name: '', // Unique - clear
      modelo_id: ups.modelo_id?.toString() || '', // Copy
      nro_serie: '', // Unique - clear
      ip_mgmt: '', // Unique - clear
      site: ups.site?.toString() || '', // Copy
      ubicacion: ups.ubicacion?.toString() || '', // Copy
      id_proveedor: ups.id_proveedor?.toString() || '', // Copy
      costo: ups.costo?.toString() || '', // Copy
      ultimo_service: '', // Clear
      observations: ups.observations || '', // Copy
      status_id: ups.status_id?.toString() || '' // Copy
    });
    setEditModal({ open: true, ups: null, isCopy: true });
  };

  // Create new
  const handleCreate = () => {
    setFormData({
      name: '', modelo_id: '', nro_serie: '', ip_mgmt: '', site: '',
      ubicacion: '', id_proveedor: '', costo: '', ultimo_service: '', observations: '', status_id: ''
    });
    setEditModal({ open: true, ups: null, isCopy: false });
  };

  // Save
  const handleSave = async () => {
    if (!formData.name.trim()) {
      toast.error('El nombre es requerido');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...formData,
        modelo_id: formData.modelo_id ? parseInt(formData.modelo_id) : null,
        site: formData.site ? parseInt(formData.site) : null,
        ubicacion: formData.ubicacion ? parseInt(formData.ubicacion) : null,
        id_proveedor: formData.id_proveedor ? parseInt(formData.id_proveedor) : null,
        costo: formData.costo ? parseFloat(formData.costo) : null,
        ultimo_service: formData.ultimo_service || null,
        status_id: formData.status_id ? parseInt(formData.status_id) : null
      };

      if (editModal.ups && !editModal.isCopy) {
        await api.put(`/assets/ups/${editModal.ups.id}`, payload);
        toast.success('UPS actualizado exitosamente');
      } else {
        await api.post('/assets/ups', payload);
        toast.success('UPS creado exitosamente');
      }
      setEditModal({ open: false, ups: null, isCopy: false });
      fetchUpsList();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  // Delete
  const handleDelete = async () => {
    try {
      await api.delete(`/assets/ups/${deleteModal.ups.id}`);
      toast.success('UPS eliminado exitosamente');
      setDeleteModal({ open: false, ups: null });
      fetchUpsList();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar');
    }
  };

  // Quick create modelo
  const handleQuickCreateModelo = async (nombre, extraData) => {
    const response = await api.post('/assets/ups/quick-create/modelo', { 
      nombre,
      fabricante_id: extraData?.fabricante_id || null,
      potencia_va: extraData?.potencia_va || null
    });
    await fetchOptions();
    return response.data;
  };

  // Quick create site
  const handleQuickCreateSite = async (nombre, descripcion) => {
    const response = await api.post('/assets/ups/quick-create/site', { nombre, descripcion });
    await fetchOptions();
    return response.data;
  };

  // Quick create ubicacion
  const handleQuickCreateUbicacion = async (nombre, descripcion) => {
    const response = await api.post('/assets/ups/quick-create/ubicacion', { nombre, descripcion });
    await fetchOptions();
    return response.data;
  };

  // Quick create proveedor
  const handleQuickCreateProveedor = async (nombre, descripcion) => {
    const response = await api.post('/assets/ups/quick-create/proveedor', { nombre, descripcion });
    await fetchOptions();
    return response.data;
  };

  // Get unique fabricantes for filter
  const uniqueFabricantes = [...new Set(options.modelos.map(m => m.marca).filter(Boolean))];

  if (!canRead) {
    return (
      <Layout>
        <div className="text-center py-12">
          <p className="text-muted-foreground">No tienes permisos para ver UPS</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="page-header flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="page-title">UPS</h1>
            <p className="page-subtitle">Gestion de sistemas de alimentacion ininterrumpida</p>
          </div>
          <div className="flex items-center gap-2">
            <ExportButton 
              endpoint="/assets/ups"
              filters={{ search: filters.search }}
              filename="ups"
            />
            {canCreate && (
              <Button onClick={handleCreate} className="btn btn-primary" data-testid="new-ups-btn">
                <Plus className="h-4 w-4 mr-2" />
                Nuevo UPS
              </Button>
            )}
          </div>
        </div>

        {/* Stats - Clickeable para filtrar */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3" data-testid="ups-stats">
          <div 
            className={`stats-card flex items-center gap-3 cursor-pointer transition-all hover:ring-2 hover:ring-primary/50 ${activeStatFilter === 'all' ? 'ring-2 ring-primary' : ''}`}
            onClick={() => handleStatFilter('all')}
            data-testid="stats-total"
          >
            <BatteryCharging className="h-6 w-6 text-primary flex-shrink-0" strokeWidth={1.5} />
            <div>
              <div className="stats-card-value">{stats.total}</div>
              <div className="stats-card-label">Total</div>
            </div>
          </div>
          <div 
            className={`stats-card flex items-center gap-3 cursor-pointer transition-all hover:ring-2 hover:ring-green-500/50 ${activeStatFilter === 'apc' ? 'ring-2 ring-green-500' : ''}`}
            onClick={() => handleStatFilter('apc')}
            data-testid="stats-apc"
          >
            <Zap className="h-6 w-6 text-green-500 flex-shrink-0" strokeWidth={1.5} />
            <div>
              <div className="stats-card-value text-green-500">{stats.apc}</div>
              <div className="stats-card-label">APC</div>
            </div>
          </div>
          <div 
            className={`stats-card flex items-center gap-3 cursor-pointer transition-all hover:ring-2 hover:ring-blue-500/50 ${activeStatFilter === 'eaton' ? 'ring-2 ring-blue-500' : ''}`}
            onClick={() => handleStatFilter('eaton')}
            data-testid="stats-eaton"
          >
            <Zap className="h-6 w-6 text-blue-500 flex-shrink-0" strokeWidth={1.5} />
            <div>
              <div className="stats-card-value text-blue-500">{stats.eaton}</div>
              <div className="stats-card-label">Eaton</div>
            </div>
          </div>
          <div 
            className={`stats-card flex items-center gap-3 cursor-pointer transition-all hover:ring-2 hover:ring-gray-500/50 ${activeStatFilter === 'otros' ? 'ring-2 ring-gray-500' : ''}`}
            onClick={() => handleStatFilter('otros')}
            data-testid="stats-otros"
          >
            <Zap className="h-6 w-6 text-gray-500 flex-shrink-0" strokeWidth={1.5} />
            <div>
              <div className="stats-card-value text-gray-500">{stats.otros}</div>
              <div className="stats-card-label">Otros</div>
            </div>
          </div>
          <div className="stats-card flex items-center gap-3" data-testid="stats-potencia">
            <Zap className="h-6 w-6 text-yellow-500 flex-shrink-0" strokeWidth={1.5} />
            <div>
              <div className="stats-card-value text-yellow-500">{stats.potencia_total_kva}</div>
              <div className="stats-card-label">KVA Total</div>
            </div>
          </div>
        </div>

        {/* Table */}
        <Card className="card-subtle overflow-hidden">
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary border-t-transparent"></div>
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="table-subtle">
                    <thead>
                      <tr>
                        <th className="w-10">
                          <Checkbox
                            checked={isAllSelected}
                            indeterminate={isSomeSelected}
                            onCheckedChange={toggleAll}
                            aria-label="Seleccionar todos"
                          />
                        </th>
                        <th 
                          className="cursor-pointer hover:bg-muted/50 select-none"
                          onClick={() => handleSort('name')}
                        >
                          <div className="flex items-center gap-1">
                            NOMBRE
                            <SortIcon field="name" />
                          </div>
                        </th>
                        <th 
                          className="cursor-pointer hover:bg-muted/50 select-none"
                          onClick={() => handleSort('ip_mgmt')}
                        >
                          <div className="flex items-center gap-1">
                            IP GESTION
                            <SortIcon field="ip_mgmt" />
                          </div>
                        </th>
                        <th>NRO SERIE</th>
                        <th className="text-center">FABRICANTE</th>
                        <th>MODELO</th>
                        <th>POTENCIA</th>
                        <th>SITE</th>
                        <th>ESTADO</th>
                        <th className="w-28 text-center">ACCIONES</th>
                      </tr>
                      <tr className="bg-muted/30">
                        <th className="w-10 py-2 px-4"></th>
                        <th className="py-2 px-4">
                          <Input
                            placeholder="Buscar en todos los campos..."
                            value={searchInput}
                            onChange={(e) => setSearchInput(e.target.value)}
                            className="h-7 text-xs bg-background"
                            data-testid="filter-search"
                          />
                        </th>
                        <th className="py-2 px-4"></th>
                        <th className="py-2 px-4"></th>
                        <th className="py-2 px-4">
                          <Select
                            value={filters.fabricante || undefined}
                            onValueChange={(v) => handleFilterChange('fabricante', v === "todos" ? "" : v)}
                          >
                            <SelectTrigger className="h-7 text-xs bg-background">
                              <SelectValue placeholder="Todos" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="todos">Todos</SelectItem>
                              {filterOptions.fabricantes?.map(fab => (
                                <SelectItem key={fab.id} value={fab.nombre}>{fab.nombre}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </th>
                        <th className="py-2 px-4"></th>
                        <th className="py-2 px-4"></th>
                        <th className="py-2 px-4">
                          <Select
                            value={filters.site || undefined}
                            onValueChange={(v) => handleFilterChange('site', v === "todos" ? "" : v)}
                          >
                            <SelectTrigger className="h-7 text-xs bg-background">
                              <SelectValue placeholder="Todos" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="todos">Todos</SelectItem>
                              {filterOptions.sites?.map(site => (
                                <SelectItem key={site.id} value={site.id.toString()}>{site.nombre}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </th>
                        <th className="py-2 px-4">
                          <Select
                            value={filters.status_id || undefined}
                            onValueChange={(v) => handleFilterChange('status_id', v === "todos" ? "" : v)}
                          >
                            <SelectTrigger className="h-7 text-xs bg-background">
                              <SelectValue placeholder="Todos" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="todos">Todos</SelectItem>
                              {filterOptions.estados?.map(e => (
                                <SelectItem key={e.id} value={e.id.toString()}>{e.nombre}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </th>
                        <th className="w-28"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {upsList.length === 0 ? (
                        <tr>
                          <td colSpan={10}>
                            <div className="empty-state">
                              <BatteryCharging className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
                              <p className="empty-state-text">No se encontraron UPS</p>
                            </div>
                          </td>
                        </tr>
                      ) : upsList.map((ups) => (
                        <tr key={ups.id} data-testid={`ups-row-${ups.id}`} className={isSelected(ups.id) ? 'bg-primary/5' : ''}>
                          <td className="w-10">
                            <Checkbox
                              checked={isSelected(ups.id)}
                              onCheckedChange={() => toggleRow(ups.id)}
                              aria-label={`Seleccionar ${ups.name}`}
                            />
                          </td>
                          <td className="font-medium text-primary">{ups.name}</td>
                          <td className="font-mono text-sm text-muted-foreground">{ups.ip_mgmt || '-'}</td>
                          <td className="font-mono text-xs text-muted-foreground">{ups.nro_serie || '-'}</td>
                          <td className="text-center">{getFabricanteBadge(ups.fabricante_nombre)}</td>
                          <td className="text-muted-foreground">{ups.modelo_nombre || '-'}</td>
                          <td className="text-muted-foreground">{ups.potencia_va ? `${ups.potencia_va} VA` : '-'}</td>
                          <td className="text-muted-foreground">{ups.site_nombre || '-'}</td>
                          <td>
                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                              ups.status_name?.toLowerCase().includes('activo') 
                                ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                                : ups.status_name?.toLowerCase().includes('baja') || ups.status_name?.toLowerCase().includes('inactivo')
                                ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'
                                : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400'
                            }`}>
                              {ups.status_name || 'Sin estado'}
                            </span>
                          </td>
                          <td className="w-28">
                            <div className="flex justify-center gap-0.5">
                              <button
                                onClick={() => handleView(ups)}
                                className="action-btn"
                                title="Ver detalle"
                                data-testid={`view-ups-${ups.id}`}
                              >
                                <Eye className="h-4 w-4" />
                              </button>
                              <button
                                onClick={() => canUpdate && handleEdit(ups)}
                                className={`action-btn action-btn-edit ${!canUpdate ? 'opacity-50' : ''}`}
                                disabled={!canUpdate}
                                title="Editar"
                                data-testid={`edit-ups-${ups.id}`}
                              >
                                <Edit className="h-4 w-4" />
                              </button>
                              <button
                                onClick={() => canCreate && handleCopy(ups)}
                                className={`action-btn action-btn-copy ${!canCreate ? 'opacity-50' : ''}`}
                                disabled={!canCreate}
                                title="Copiar"
                                data-testid={`copy-ups-${ups.id}`}
                              >
                                <Copy className="h-4 w-4" />
                              </button>
                              <button
                                onClick={() => canDelete && setDeleteModal({ open: true, ups })}
                                className={`action-btn action-btn-delete ${!canDelete ? 'opacity-50' : ''}`}
                                disabled={!canDelete}
                                title="Eliminar"
                                data-testid={`delete-ups-${ups.id}`}
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                <div className="flex items-center justify-between px-4 py-3 border-t border-border">
                  <div className="flex items-center gap-4">
                    <div className="text-sm text-muted-foreground">
                      {total > 0 ? `${(page - 1) * pageSize + 1} - ${Math.min(page * pageSize, total)} de ${total}` : '0 registros'}
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-muted-foreground">Mostrar:</span>
                      <Select
                        value={pageSize.toString()}
                        onValueChange={(value) => { setPageSize(parseInt(value)); setPage(1); }}
                      >
                        <SelectTrigger className="h-8 w-20">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="10">10</SelectItem>
                          <SelectItem value="25">25</SelectItem>
                          <SelectItem value="50">50</SelectItem>
                          <SelectItem value="100">100</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(p => Math.max(1, p - 1))}
                      disabled={page === 1}
                      className="h-8 w-8 p-0"
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <span className="text-sm text-muted-foreground">{page} / {totalPages}</span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                      disabled={page === totalPages}
                      className="h-8 w-8 p-0"
                    >
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* View Modal */}
        <Dialog open={viewModal.open} onOpenChange={(open) => setViewModal({ open, ups: null })}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <BatteryCharging className="h-5 w-5 text-primary" />
                {viewModal.ups?.name}
              </DialogTitle>
            </DialogHeader>
            {viewModal.ups && (
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-3">
                  <div>
                    <Label className="text-xs text-muted-foreground">Nombre</Label>
                    <p className="font-medium">{viewModal.ups.name}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">IP Gestion</Label>
                    <p className="font-mono">{viewModal.ups.ip_mgmt || '-'}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Nro Serie</Label>
                    <p className="font-mono text-sm">{viewModal.ups.nro_serie || '-'}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Fabricante</Label>
                    <div className="mt-1">{getFabricanteBadge(viewModal.ups.fabricante_nombre)}</div>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Modelo</Label>
                    <p className="text-sm">{viewModal.ups.modelo_nombre || '-'}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Potencia</Label>
                    <p className="text-sm">{viewModal.ups.potencia_va ? `${viewModal.ups.potencia_va} VA` : '-'}</p>
                  </div>
                </div>
                <div className="space-y-3">
                  <div>
                    <Label className="text-xs text-muted-foreground">Site</Label>
                    <p>{viewModal.ups.site_nombre || '-'}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Ubicacion</Label>
                    <p>{viewModal.ups.ubicacion_nombre || '-'}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Proveedor</Label>
                    <p>{viewModal.ups.proveedor_nombre || '-'}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Costo</Label>
                    <p>{viewModal.ups.costo ? `$${viewModal.ups.costo.toLocaleString()}` : '-'}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Estado</Label>
                    <p>
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                        viewModal.ups.status_name?.toLowerCase().includes('activo') 
                          ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                          : viewModal.ups.status_name?.toLowerCase().includes('baja') || viewModal.ups.status_name?.toLowerCase().includes('inactivo')
                          ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'
                          : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400'
                      }`}>
                        {viewModal.ups.status_name || 'Sin estado'}
                      </span>
                    </p>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Ultimo Service</Label>
                    <p>{viewModal.ups.ultimo_service || '-'}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Observaciones</Label>
                    <p className="text-sm text-muted-foreground">{viewModal.ups.observations || '-'}</p>
                  </div>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Edit/Create/Copy Modal */}
        <Dialog open={editModal.open} onOpenChange={(open) => setEditModal({ open, ups: null, isCopy: false })}>
          <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <BatteryCharging className="h-5 w-5 text-primary" />
                {editModal.isCopy ? 'Copiar UPS' : editModal.ups ? 'Editar UPS' : 'Nuevo UPS'}
              </DialogTitle>
            </DialogHeader>
            
            <div className="grid grid-cols-2 gap-4">
              {/* Unique fields */}
              <div className={`space-y-1 ${editModal.isCopy ? 'ring-2 ring-yellow-500/50 rounded-md p-2 bg-yellow-500/5' : ''}`}>
                <Label className="text-xs">Nombre *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="Nombre del UPS"
                  data-testid="input-name"
                />
                {editModal.isCopy && <p className="text-xs text-yellow-600">Campo unico</p>}
              </div>
              
              <div className={`space-y-1 ${editModal.isCopy ? 'ring-2 ring-yellow-500/50 rounded-md p-2 bg-yellow-500/5' : ''}`}>
                <Label className="text-xs">IP Gestion</Label>
                <Input
                  value={formData.ip_mgmt}
                  onChange={(e) => setFormData(prev => ({ ...prev, ip_mgmt: e.target.value }))}
                  placeholder="192.168.1.100"
                  data-testid="input-ip"
                />
                {editModal.isCopy && <p className="text-xs text-yellow-600">Campo unico</p>}
              </div>
              
              <div className={`space-y-1 ${editModal.isCopy ? 'ring-2 ring-yellow-500/50 rounded-md p-2 bg-yellow-500/5' : ''}`}>
                <Label className="text-xs">Nro Serie</Label>
                <Input
                  value={formData.nro_serie}
                  onChange={(e) => setFormData(prev => ({ ...prev, nro_serie: e.target.value }))}
                  placeholder="Serial number"
                  data-testid="input-serie"
                />
                {editModal.isCopy && <p className="text-xs text-yellow-600">Campo unico</p>}
              </div>

              {/* Copy fields */}
              <div className={`space-y-1 ${editModal.isCopy ? 'ring-2 ring-green-500/50 rounded-md p-2 bg-green-500/5' : ''}`}>
                <Label className="text-xs">Modelo</Label>
                <SelectWithCreate
                  value={formData.modelo_id}
                  onValueChange={(v) => setFormData(prev => ({ ...prev, modelo_id: v }))}
                  options={options.modelos}
                  placeholder="Seleccionar modelo"
                  onCreate={handleQuickCreateModelo}
                  createLabel="modelo"
                />
                {editModal.isCopy && <p className="text-xs text-green-600">Copiado</p>}
              </div>
              
              <div className={`space-y-1 ${editModal.isCopy ? 'ring-2 ring-green-500/50 rounded-md p-2 bg-green-500/5' : ''}`}>
                <Label className="text-xs">Site</Label>
                <SelectWithCreate
                  value={formData.site}
                  onValueChange={(v) => setFormData(prev => ({ ...prev, site: v }))}
                  options={options.sites}
                  placeholder="Seleccionar site"
                  onCreate={handleQuickCreateSite}
                  createLabel="site"
                />
                {editModal.isCopy && <p className="text-xs text-green-600">Copiado</p>}
              </div>
              
              <div className={`space-y-1 ${editModal.isCopy ? 'ring-2 ring-green-500/50 rounded-md p-2 bg-green-500/5' : ''}`}>
                <Label className="text-xs">Ubicacion</Label>
                <SelectWithCreate
                  value={formData.ubicacion}
                  onValueChange={(v) => setFormData(prev => ({ ...prev, ubicacion: v }))}
                  options={options.ubicaciones}
                  placeholder="Seleccionar ubicacion"
                  onCreate={handleQuickCreateUbicacion}
                  createLabel="ubicación"
                />
                {editModal.isCopy && <p className="text-xs text-green-600">Copiado</p>}
              </div>
              
              <div className={`space-y-1 ${editModal.isCopy ? 'ring-2 ring-green-500/50 rounded-md p-2 bg-green-500/5' : ''}`}>
                <Label className="text-xs">Proveedor</Label>
                <SelectWithCreate
                  value={formData.id_proveedor}
                  onValueChange={(v) => setFormData(prev => ({ ...prev, id_proveedor: v }))}
                  options={options.proveedores}
                  placeholder="Seleccionar proveedor"
                  onCreate={handleQuickCreateProveedor}
                  createLabel="proveedor"
                />
                {editModal.isCopy && <p className="text-xs text-green-600">Copiado</p>}
              </div>
              
              <div className={`space-y-1 ${editModal.isCopy ? 'ring-2 ring-green-500/50 rounded-md p-2 bg-green-500/5' : ''}`}>
                <Label className="text-xs">Costo</Label>
                <Input
                  type="number"
                  value={formData.costo}
                  onChange={(e) => setFormData(prev => ({ ...prev, costo: e.target.value }))}
                  placeholder="0.00"
                  data-testid="input-costo"
                />
                {editModal.isCopy && <p className="text-xs text-green-600">Copiado</p>}
              </div>
              
              <div className={`space-y-1 ${editModal.isCopy ? 'ring-2 ring-green-500/50 rounded-md p-2 bg-green-500/5' : ''}`}>
                <Label className="text-xs">Estado</Label>
                <Select
                  value={formData.status_id || "none"}
                  onValueChange={(v) => setFormData(prev => ({ ...prev, status_id: v === "none" ? "" : v }))}
                >
                  <SelectTrigger data-testid="select-status">
                    <SelectValue placeholder="Seleccionar estado" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Sin estado</SelectItem>
                    {options.estados?.map(e => (
                      <SelectItem key={e.id} value={e.id.toString()}>{e.nombre}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {editModal.isCopy && <p className="text-xs text-green-600">Copiado</p>}
              </div>
              
              <div className="space-y-1">
                <Label className="text-xs">Ultimo Service</Label>
                <Input
                  type="date"
                  value={formData.ultimo_service}
                  onChange={(e) => setFormData(prev => ({ ...prev, ultimo_service: e.target.value }))}
                  data-testid="input-service"
                />
              </div>
              
              <div className={`col-span-2 space-y-1 ${editModal.isCopy ? 'ring-2 ring-green-500/50 rounded-md p-2 bg-green-500/5' : ''}`}>
                <Label className="text-xs">Observaciones</Label>
                <Textarea
                  value={formData.observations}
                  onChange={(e) => setFormData(prev => ({ ...prev, observations: e.target.value }))}
                  placeholder="Notas adicionales..."
                  rows={2}
                  data-testid="input-observations"
                />
                {editModal.isCopy && <p className="text-xs text-green-600">Copiado</p>}
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setEditModal({ open: false, ups: null, isCopy: false })}>
                Cancelar
              </Button>
              <Button onClick={handleSave} disabled={saving} className="btn btn-primary">
                {saving ? 'Guardando...' : 'Guardar'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Modal */}
        <Dialog open={deleteModal.open} onOpenChange={(open) => setDeleteModal({ open, ups: null })}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-destructive">
                <Trash2 className="h-5 w-5" />
                Eliminar UPS
              </DialogTitle>
            </DialogHeader>
            <p className="text-muted-foreground">
              Estas seguro que deseas eliminar el UPS <strong>{deleteModal.ups?.name}</strong>?
            </p>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDeleteModal({ open, ups: null })}>
                Cancelar
              </Button>
              <Button variant="destructive" onClick={handleDelete}>
                Eliminar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Bulk Edit Bar - Site y Estado */}
        <BulkEditBar
          selectedIds={selectedIds}
          onClear={clearSelection}
          onSuccess={() => { fetchUpsList(); fetchStats(); }}
          endpoint="/assets/ups"
          options={{
            sites: filterOptions.sites?.map(s => ({ id: s.id, nombre: s.nombre })),
            estados: filterOptions.estados?.map(e => ({ id: e.id, nombre: e.nombre }))
          }}
          fields={['site_id', 'status_id']}
        />
      </div>
    </Layout>
  );
};

export default Ups;
